// **REACT IMPORTS
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

// **THIRD PARTY IMPORTS
import debounce from 'lodash.debounce'
import { Device } from 'mediasoup-client'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { io } from 'socket.io-client'
import { v4 } from 'uuid'

// **ENV IMPORTS
import { MEDIA_SERVER_URL_MAP } from '../env/env'

// **Utils Imports
import {
  audioStreamCodes,
  interviewVideoStreamCodecs,
  ROLES,
  SUBSCRIPTION_PLANS,
  videoStreamCodecs
} from '../utils/constant'
import {
  formatProducerObj,
  getAudioDeviceId,
  getUserDetails,
  setAudioDeviceId,
  setGetAudioVideoLoading
} from '../utils/utils'

// ** Component Imports
import ErrorBoundary from '../pages/error'

// **Notification Sound Import
import NotificationSound from '../assets/audio/notification.mp3'

// **Redux Imports
import { useSelector } from 'react-redux'

// **API Imports
import { candidateAttemptEvents } from '../axios/api/candidate'

// **Context Imports
import { SocketContext } from './socket'

//Icon

export const MeetingContext = createContext(null)

const MeetingProvider = ({ children }) => {
  // Hooks
  const [searchParams] = useSearchParams()

  //Context Imports
  const {
    isSocketConnected,
    socketAuthenticated,
    socket: editorSocket,
    getMediaServerEndPoint
  } = useContext(SocketContext)

  // Refs
  const device = useRef(null)
  const socket = useRef(null)
  const producerIds = useRef([])
  const consumerQueue = useRef([])
  const videoProducer = useRef(null)
  const needVideoRef = useRef(false)
  const devicesStateRef = useRef([])
  const audioProducer = useRef(null)
  const localStreamRef = useRef(null)
  const chatRoomNameRef = useRef(null)
  const consumingTransport = useRef({})
  const socketRetryCountRef = useRef(0)
  const excludeEndpointsRef = useRef([])
  const consumerTransport = useRef(null)
  const currentlySupervising = useRef([])
  const producersTransport = useRef(null)
  const routerCapabilities = useRef(null)
  const screenShareProducer = useRef(null)
  const previousMetadataRef = useRef(null)
  const webcamMediaRecordRef = useRef(null)
  const screenMediaRecorderRef = useRef(null)
  const localScreenShareStreamRef = useRef(null)
  const screenShareMutedTimeoutRef = useRef(null)
  const selectedAudioInputDeviceIdRef = useRef(null)
  const selectedAudioInputGroupIdRef = useRef(null)
  const currentStageAttempIds = useRef({ attemptId: null, stageId: null })

  // Redux
  const currentlyActivePlan = useSelector(
    (state) => state.auth.currentlyActivePlan
  )

  // State
  const [chats, setChats] = useState({})
  const [peerInfo, setPeeerInfo] = useState({})
  const [mediaState, setMediaState] = useState({
    audio: false,
    webcam: false,
    screen: false,
    handRaised: false
  })
  const [newStream, setNewStream] = useState({})
  const [updatedChats, setUpdatedChats] = useState({})
  const [localStream, setLocalStream] = useState(null)
  const [supervisedUser, setSupervisedUser] = useState([])
  const [userRoomJoined, setUserRoomJoined] = useState(false)
  const [audioInputDevices, setAudioInputDevices] = useState([])
  const [connectedUserList, setConnectedUserList] = useState([])
  const [endInterviewRoom, setEndInterviewRoom] = useState(false)
  const [audioOutputDevices, setAudioOutputDevices] = useState([])
  const [isOnline, setIsOnline] = useState(window.navigator.onLine)
  const [selectedAudioInputDeviceId, setSelectedAudioInputDeviceId] =
    useState(null)
  const [localScreenShareStream, setLocalScreenShareStream] = useState(null)
  const [producersTransportState, setProducersTransportState] = useState(null)
  const [recallGetEndPoint, setRecallGetEndpoint] = useState({
    call: false,
    stageId: null,
    attemptId: null,
    excludeEndpoints: []
  })

  // New States

  // Variable
  const attemptId = useMemo(() => {
    return searchParams.get('attemptId') || null
  }, [searchParams])

  // Functions
  const handleEndInterviewForAll = useCallback((roomName) => {
    socket?.current?.emit('end-interview-room', { roomName })
  }, [])

  const handleChatRoomName = useCallback((roomName) => {
    chatRoomNameRef.current = roomName
  }, [])

  const handleUpdatePeerInfoProperties = useCallback((key, value) => {
    setPeeerInfo((prev) => ({
      ...prev,
      properties: { ...(prev?.properties || {}), [key]: value }
    }))
  }, [])

  const handleProducerPaused = useCallback((paused, type) => {
    setMediaState((prev) => ({ ...prev, [type]: paused }))
  }, [])

  const endMediaProducer = useCallback((mediaType) => {
    const producerTypeToClose =
      mediaType === 'screen' ? ['screen'] : ['webcam', 'audio']

    producerTypeToClose?.forEach((type) => {
      socket?.current?.emit('producer-close', { type })
    })

    if (mediaType === 'screen') {
      if (screenMediaRecorderRef.current) {
        screenMediaRecorderRef.current?.stop()
        screenMediaRecorderRef.current = null
      }

      //Closing Producer
      screenShareProducer?.current?.close()

      screenShareProducer.current = null

      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('mute', screenShareMuted)
      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('unmute', screenShareUnmuted)
      localScreenShareStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('ended', screenShareEnded)

      localScreenShareStreamRef?.current
        ?.getTracks()
        ?.forEach((track) => track.stop())

      localScreenShareStreamRef.current = null
      setLocalScreenShareStream(null)
    } else {
      if (webcamMediaRecordRef.current) {
        webcamMediaRecordRef.current?.stop()
        webcamMediaRecordRef.current = null
      }

      //Closing Producers
      audioProducer?.current?.close()
      videoProducer?.current?.close()

      audioProducer.current = null
      videoProducer.current = null

      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('mute', webcamAudioMuted)
      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('unmute', webcamAudioUnmuted)
      localStreamRef?.current
        ?.getAudioTracks()?.[0]
        ?.removeEventListener('ended', webcamAudioEnded)

      localStreamRef?.current
        ?.getVideoTracks()?.[0]
        ?.removeEventListener('ended', webcamVideoEnded)

      localStreamRef?.current?.getTracks()?.forEach((track) => track.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }

    setPeeerInfo((prev) => ({
      ...prev,
      ...(mediaType === 'screen'
        ? { screenStreamId: null }
        : { webcamAudioStreamId: null, webcamVideoStreamId: null }),
      properties: {
        ...(prev?.properties || {}),
        producers:
          prev?.properties?.producers?.filter(
            (producer) => !producerTypeToClose.includes(producer.kind)
          ) || []
      }
    }))

    producerIds.current = producerIds.current.filter(
      (producer) => !producerTypeToClose.includes(producer.kind)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const webcamAudioMuted = useCallback(() => {
    handleProducerPaused(true, 'audio')
  }, [handleProducerPaused])

  const webcamAudioUnmuted = useCallback(() => {
    handleProducerPaused(false, 'audio')
  }, [handleProducerPaused])

  const webcamAudioEnded = useCallback(() => {
    handleProducerPaused(true, 'audio')
    endMediaProducer('audio')
  }, [endMediaProducer, handleProducerPaused])

  const webcamVideoEnded = useCallback(() => {
    endMediaProducer('webcam')
  }, [endMediaProducer])

  const screenShareMuted = useCallback(() => {
    if (navigator?.onLine) {
      screenShareMutedTimeoutRef.current = setTimeout(() => {
        endMediaProducer('screen')
      }, 5000)
    }
  }, [endMediaProducer])

  const screenShareUnmuted = useCallback(() => {
    if (screenShareMutedTimeoutRef.current) {
      clearTimeout(screenShareMutedTimeoutRef.current)
      screenShareMutedTimeoutRef.current = null
    }
  }, [])

  const screenShareEnded = useCallback(() => {
    if (screenShareMutedTimeoutRef.current) {
      clearTimeout(screenShareMutedTimeoutRef.current)
      screenShareMutedTimeoutRef.current = null
    }
  }, [])

  const getEnumeratedDevices = useCallback(() => {
    return new Promise((resolve, reject) => {
      navigator?.mediaDevices
        ?.getUserMedia({ audio: true, video: false })
        ?.then((stream) => {
          const systemDeviceId =
            stream?.getAudioTracks()?.[0]?.getSettings()?.deviceId || null
          navigator?.mediaDevices?.enumerateDevices().then((devices) => {
            const inputs = devices?.filter(
              (device) => device?.kind === 'audioinput'
            )

            const outputs = devices?.filter(
              (device) => device?.kind === 'audiooutput'
            )

            setAudioInputDevices(inputs)
            setAudioOutputDevices(outputs)

            resolve({
              inputs,
              outputs,
              systemDeviceId
            })
          })
          stream.getTracks().forEach(function (track) {
            track.stop()
          })
        })
        .catch((err) => {
          reject(err)
        })
    })
  }, [])

  const onRefreshStreamClick = useCallback((userObj) => {
    socket?.current?.emit('refresh-candidate', {
      hostname: userObj?.hostname,
      worker: userObj?.worker,
      roomName: userObj?.roomName,
      socketId: userObj?.socketId
    })
  }, [])

  const handleMuteUser = useCallback((userObj) => {
    socket?.current?.emit('mute-user', {
      hostname: userObj?.hostname,
      worker: userObj?.worker,
      roomName: userObj?.roomName,
      socketId: userObj?.socketId
    })
  }, [])

  const playNotificationSound = useCallback(() => {
    const audio = new Audio(NotificationSound)
    audio.play().catch((error) => {})
  }, [])

  const showNotification = useCallback((chat) => {
    const title = chat?.sendBy?.name || ''
    const message = chat?.text || ''
    toast.info(`${title} : ${message}`)
  }, [])

  const groupReceiveMessage = useCallback((chats) => {
    const obj = {}
    const details = getUserDetails()
    chats.forEach((chat) => {
      if (
        !chatRoomNameRef.current ||
        chatRoomNameRef.current === chat?.properties?.roomName
      ) {
        if (
          details?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
          chat?.sendBy?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
          chat?.sendBy?.email === details?.sub
        ) {
          if (!chat.sendTo) {
            obj['@all'] = [...(obj?.['@all'] || []), chat]
          } else if (chat?.sendTo && chat?.sendTo?.email === details?.sub) {
            obj[chat?.sendBy?.email] = [
              ...(obj?.[chat?.sendBy?.email] || []),
              chat
            ]
          } else if (chat?.sendTo && chat?.sendBy?.email === details?.sub) {
            obj[chat?.sendTo?.email] = [
              ...(obj?.[chat?.sendTo?.email] || []),
              chat
            ]
          }
        }
      }
    })
    setChats(obj)
    setUpdatedChats(obj)
  }, [])

  const receiveChatMessage = useCallback(
    (chat) => {
      if (
        !chatRoomNameRef.current ||
        chatRoomNameRef.current === chat?.properties?.roomName
      ) {
        const details = getUserDetails()

        function playSendNotification() {
          if (
            chat.sendBy?.email !== details?.sub ||
            chat?.sendBy?.roles !== details?.roles
          ) {
            playNotificationSound()
            showNotification(chat)
          }
        }

        setUpdatedChats((prev) => {
          const obj = { ...prev }
          if (
            details?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
            chat?.sendBy?.roles?.toLowerCase() !== ROLES.CANDIDATE ||
            chat?.sendBy?.email === details?.sub
          ) {
            if (!chat.sendTo) {
              obj['@all'] = [...(obj?.['@all'] || []), chat]
              playSendNotification()
            } else if (chat?.sendTo && chat?.sendTo?.email === details?.sub) {
              obj[chat?.sendBy?.email] = [
                ...(obj?.[chat?.sendBy?.email] || []),
                chat
              ]
              playSendNotification()
            } else if (chat?.sendTo && chat?.sendBy?.email === details?.sub) {
              obj[chat?.sendTo?.email] = [
                ...(obj?.[chat?.sendTo?.email] || []),
                chat
              ]
              playSendNotification()
            }
          }

          return obj
        })
      }
    },
    [playNotificationSound, showNotification]
  )

  const handleMeetingEditorSocketMessages = useCallback(
    (event) => {
      const data = JSON.parse(event?.data || null)
      if (data?.method === 'media-server/worker-metadata') {
        const strifyData = JSON.stringify(data?.parameters)
        if (previousMetadataRef.current !== strifyData) {
          previousMetadataRef.current = strifyData
          const formattedProducer = formatProducerObj(data?.parameters)
          setConnectedUserList(formattedProducer || [])
        }
      } else if (data?.method === 'chat-room/receive-message') {
        receiveChatMessage(data?.parameters)
      } else if (data?.method === 'chat-room/get-all-messages-response') {
        groupReceiveMessage(data?.parameters?.messages || [])
      }
    },
    [receiveChatMessage, groupReceiveMessage]
  )

  const sendMessage = useCallback(
    (method, parameters = {}) => {
      if (editorSocket && editorSocket.readyState === WebSocket.OPEN) {
        editorSocket.send(JSON.stringify({ id: v4(), method, parameters }))
      }
    },
    [editorSocket]
  )

  const sendCandidateEvent = useCallback(
    (event, eventDetails) => {
      if (attemptId) {
        const payload = {
          event,
          attemptId,
          eventDetails
        }
        candidateAttemptEvents(payload)
      }
    },
    [attemptId]
  )

  const getAllChatMessages = useCallback(() => {
    sendMessage('chat-room/get-all-messages', {})
  }, [sendMessage])

  const handleUpdateChats = useCallback((key, value) => {
    if (value?.length > 0) {
      setChats((prev) => {
        return { ...prev, [key]: value }
      })
    }
  }, [])

  const sendChatMessage = useCallback(
    ({ chat }) => {
      sendMessage('chat-room/send-message', chat)
    },
    [sendMessage]
  )

  const handleSuperviseCandidateSocket = useCallback(
    (candidateSocketId, isSupervised) => {
      socket?.current?.emit('supervise-candidate', {
        candidateSocketId,
        isSupervised
      })

      if (isSupervised) {
        currentlySupervising.current = [
          ...currentlySupervising.current,
          candidateSocketId
        ]
      } else {
        currentlySupervising.current = currentlySupervising.current.filter(
          (id) => id !== candidateSocketId
        )
      }
    },
    []
  )

  const connectRecvTransport = useCallback(
    async (
      remoteProducerId,
      serverConsumerTransportId,
      hostname,
      workerId,
      roomName
    ) => {
      await socket?.current?.emit('consume', {
        rtpCapabilities: device.current.rtpCapabilities,
        remoteProducerId,
        serverConsumerTransportId,
        hostname,
        workerId,
        roomName
      })
    },
    []
  )

  const closeConsumer = useCallback((remoteProducerId) => {
    const producerToClose = consumingTransport?.current?.[remoteProducerId]

    if (producerToClose) {
      producerToClose?.consumer?.close()

      delete consumingTransport?.current?.[remoteProducerId]

      setPeeerInfo((prev) => ({
        ...prev,
        properties: {
          ...(prev?.properties || {}),
          consumers:
            prev?.properties?.consumers?.filter(
              (c) => c?.producerId !== remoteProducerId
            ) || []
        }
      }))

      setNewStream((prev) => {
        const newData = { ...prev }
        delete newData?.[remoteProducerId]
        return newData
      })
    }
  }, [])

  const signalNewConsumerTransport = useCallback(
    async (remoteProducerId, candidateObj, type) => {
      const filterConsumingTransport =
        consumingTransport?.current?.[remoteProducerId]

      if (filterConsumingTransport) return

      consumingTransport.current[remoteProducerId] = {
        type,
        ...candidateObj,
        producerId: remoteProducerId
      }

      setPeeerInfo((prev) => ({
        ...prev,
        properties: {
          ...(prev?.properties || {}),
          consumers: [
            ...(prev?.properties?.consumers || []),
            {
              producerId: remoteProducerId,
              socketId: candidateObj?.socketId,
              email: candidateObj?.email
            }
          ]
        }
      }))

      if (!consumerTransport?.current?.consumerTransport) {
        consumerQueue.current = [
          ...(consumerQueue.current || []),
          remoteProducerId
        ]
      } else {
        connectRecvTransport(
          remoteProducerId,
          consumerTransport.current.params.id,
          candidateObj?.hostname,
          candidateObj?.worker,
          candidateObj?.roomName
        )
      }
    },
    [connectRecvTransport]
  )

  const intialiseRecordingData = useCallback((type, id) => {
    const isWebmSupported = MediaRecorder.isTypeSupported(
      'video/webm;codecs=vp8'
    )

    socket?.current?.emit('stream-details', {
      id,
      type,
      fileName: `${type}.${isWebmSupported ? 'webm' : 'mp4'}`,
      timestamp: new Date().toISOString(),
      ContentType: `${isWebmSupported ? 'video/webm' : 'video/mp4'}`
    })
  }, [])

  const startSendingScreenShareData = useCallback((stream, id) => {
    screenMediaRecorderRef.current = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
        ? 'video/webm;codecs=vp8'
        : 'video/mp4',
      videoBitsPerSecond: 250000,
      audioBitsPerSecond: 64000
    })

    screenMediaRecorderRef.current.onerror = (event) => {
      console.error('MediaRecorder error:', event)
    }

    screenMediaRecorderRef.current.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        const arrayBuffer = await event.data.arrayBuffer()
        socket?.current?.emit('screen-share-data', { id, data: arrayBuffer })
      }
    }

    screenMediaRecorderRef.current.onstop = () => {
      socket?.current?.emit('end-screen-share-stream', { id })
      screenMediaRecorderRef.current = null
    }

    try {
      screenMediaRecorderRef.current.start(500) // Collect data every 1 second
    } catch (error) {
      console.error('Error starting MediaRecorder:', error)
    }
  }, [])

  const startSendingWebcamData = useCallback((stream, id) => {
    webcamMediaRecordRef.current = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/mp4;codecs=avc1',
      videoBitsPerSecond: 250000,
      audioBitsPerSecond: 64000
    })

    webcamMediaRecordRef.current.onerror = (event) => {
      console.error('MediaRecorder error:', event)
    }

    webcamMediaRecordRef.current.ondataavailable = async (event) => {
      if (event.data.size > 0) {
        const arrayBuffer = await event.data.arrayBuffer()
        socket?.current?.emit('webcam-data', { id, data: arrayBuffer })
      }
    }

    webcamMediaRecordRef.current.onstop = () => {
      socket?.current?.emit('end-webcam-stream', { id })
      webcamMediaRecordRef.current = null
    }

    try {
      webcamMediaRecordRef.current.start(500) // Collect data every 1 second
    } catch (error) {
      console.error('Error starting MediaRecorder:', error)
    }
  }, [])

  const handleAudioVideoProducer = useCallback(async () => {
    const details = getUserDetails()

    if (producersTransport.current && localStreamRef?.current) {
      audioProducer.current = await producersTransport.current.produce({
        appData: { source: 'audio' },
        track: localStreamRef.current.getAudioTracks()[0],
        stopTracks: false,
        ...audioStreamCodes
      })

      audioProducer.current.on('trackended', () => {
        endMediaProducer('audio')
      })

      audioProducer.current.on('transportclose', () => {})
    }

    if (
      localStreamRef?.current?.getVideoTracks()?.length > 0 &&
      producersTransport.current
    ) {
      videoProducer.current = await producersTransport.current.produce({
        appData: { source: 'webcam' },
        track: localStreamRef.current.getVideoTracks()[0],
        stopTracks: false,
        ...(chatRoomNameRef?.current
          ? { interviewVideoStreamCodecs }
          : { videoStreamCodecs })
      })

      videoProducer.current.on('trackended', () => {
        endMediaProducer('webcam')
      })

      videoProducer.current.on('transportclose', () => {})

      if (
        details?.roles?.toLowerCase() === ROLES.CANDIDATE &&
        (currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.GROWTH ||
          currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.PRO ||
          currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.ENTERPRISE)
      ) {
        const id = v4()
        intialiseRecordingData('webcam', id)
        sendCandidateEvent('webcamStarted', {
          timestamp: new Date().toISOString()
        })
        startSendingWebcamData(localStreamRef.current, id)
      }
    }
  }, [
    endMediaProducer,
    sendCandidateEvent,
    intialiseRecordingData,
    startSendingWebcamData,
    currentlyActivePlan
  ])

  const handleScreenShareProducer = useCallback(async () => {
    const details = getUserDetails()
    if (
      localScreenShareStreamRef?.current?.getVideoTracks()?.length > 0 &&
      producersTransport.current
    ) {
      screenShareProducer.current = await producersTransport.current.produce({
        appData: { source: 'screen' },
        track: localScreenShareStreamRef.current.getVideoTracks()[0],
        stopTracks: false,
        ...(chatRoomNameRef?.current
          ? { interviewVideoStreamCodecs }
          : { videoStreamCodecs })
      })

      screenShareProducer.current.on('trackended', () => {
        endMediaProducer('screen')
      })

      screenShareProducer.current.on('transportclose', () => {})

      if (
        details?.roles?.toLowerCase() === ROLES.CANDIDATE &&
        (currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.GROWTH ||
          currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.PRO ||
          currentlyActivePlan?.planName === SUBSCRIPTION_PLANS.ENTERPRISE)
      ) {
        const id = v4()
        intialiseRecordingData('screen', id)
        sendCandidateEvent('screenShareStarted', {
          timestamp: new Date().toISOString()
        })
        startSendingScreenShareData(localScreenShareStreamRef.current, id)
      }
    }
  }, [
    endMediaProducer,
    sendCandidateEvent,
    intialiseRecordingData,
    startSendingScreenShareData,
    currentlyActivePlan
  ])

  const createProduceTransport = useCallback(() => {
    socket?.current?.emit(
      'createWebRtcTransport',
      { consumer: false },
      async ({ params }) => {
        if (params.error) {
          return
        }

        const transport = device.current.createSendTransport(params)

        producersTransport.current = transport
        setProducersTransportState(transport)

        producersTransport.current.on(
          'connect',
          async ({ dtlsParameters }, callback, errback) => {
            try {
              await socket?.current?.emit('transport-connect', {
                dtlsParameters
              })

              callback()
            } catch (error) {
              errback(error)
            }
          }
        )

        producersTransport.current.on(
          'produce',
          async (parameters, callback, errback) => {
            try {
              await socket?.current?.emit(
                'transport-produce',
                {
                  kind: parameters.kind,
                  rtpParameters: parameters.rtpParameters,
                  appData: parameters.appData
                },
                ({ id, producersExist, appData }) => {
                  callback({ id })

                  setPeeerInfo((prev) => ({
                    ...prev,
                    [appData?.source === 'audio'
                      ? 'webcamAudioStreamId'
                      : appData?.source === 'webcam'
                      ? 'webcamVideoStreamId'
                      : 'screenStreamId']: id
                  }))

                  producerIds.current.push({
                    kind: parameters.appData.source,
                    producerId: id
                  })

                  setPeeerInfo((prev) => ({
                    ...prev,
                    properties: {
                      ...(prev?.properties || {}),
                      producers: [
                        ...(prev?.properties?.producers || []),
                        {
                          kind: parameters.appData.source,
                          producerId: id
                        }
                      ]
                    }
                  }))
                }
              )
            } catch (error) {
              errback(error)
            }
          }
        )
      }
    )
  }, [])

  const createReceiverTransport = useCallback(async () => {
    await socket?.current?.emit(
      'createWebRtcTransport',
      { consumer: true },
      ({ params }) => {
        if (params.error) {
          return
        }

        let transport
        try {
          transport = device.current.createRecvTransport(params)
        } catch (error) {
          return
        }

        transport.on(
          'connect',
          async ({ dtlsParameters }, callback, errback) => {
            try {
              await socket?.current?.emit('transport-recv-connect', {
                dtlsParameters,
                serverConsumerTransportId: params.id
              })

              callback()
            } catch (error) {
              errback(error)
            }
          }
        )

        consumerTransport.current = {
          consumerTransport: transport,
          params
        }

        if (consumerQueue?.current?.length > 0) {
          const queue = [...consumerQueue.current]
          consumerQueue.current = []

          queue?.forEach((producerId) => {
            const candidateObj =
              consumingTransport?.current?.[producerId] || null

            if (candidateObj) {
              connectRecvTransport(
                producerId,
                params.id,
                candidateObj?.hostname,
                candidateObj?.worker,
                candidateObj?.roomName
              )
            }
          })
        }
      }
    )
  }, [connectRecvTransport])

  const createDevice = useCallback(async () => {
    try {
      if (!device.current) {
        device.current = new Device()

        await device.current.load({
          routerRtpCapabilities: routerCapabilities.current
        })
      }

      createProduceTransport()
      createReceiverTransport()
      // getChatMessages()
    } catch (error) {}
  }, [createProduceTransport, createReceiverTransport])

  const getScreenShareSteam = useCallback(
    async (requiredFullShare = true) => {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          audio: false,
          video: {
            displaySurface: 'monitor',
            logicalSurface: true,
            cursor: true,

            frameRate: { max: 30 }
          }
        })

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('mute', screenShareMuted)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('unmute', screenShareUnmuted)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('ended', screenShareEnded)

        const settings = stream?.getVideoTracks()?.[0]?.getSettings()
        const originalScreenWidth =
          window.screen.width * window.devicePixelRatio
        const originalScreenHeight =
          window.screen.height * window.devicePixelRatio

        const isFullScreen =
          settings?.width === originalScreenWidth &&
          settings?.height === originalScreenHeight

        if (!requiredFullShare) {
          localScreenShareStreamRef.current = stream
          setLocalScreenShareStream(stream)
          return true
        } else if (settings?.displaySurface) {
          if (settings?.displaySurface === 'monitor') {
            localScreenShareStreamRef.current = stream
            setLocalScreenShareStream(stream)
            return true
          } else {
            stream?.getTracks()?.forEach((track) => track.stop())
            throw new Error(
              'Please share the entire screen to continue with the challenge'
            )
          }
        } else if (isFullScreen) {
          localScreenShareStreamRef.current = stream
          setLocalScreenShareStream(stream)
          return true
        } else {
          stream?.getTracks()?.forEach((track) => track.stop())
          throw new Error(
            'Please share the entire screen to continue with the challenge'
          )
        }
      } catch (error) {
        console.log('Screen Share ISSUE : ', error?.message || '')
        return false
      }
    },
    [screenShareMuted, screenShareUnmuted, screenShareEnded]
  )

  const getAudioVideoStream = useCallback(
    async (needVideo = true, deviceId, groupId) => {
      needVideoRef.current = needVideo
      try {
        let selectedGroupId = groupId
        let selectedDeviceId = deviceId

        if (!selectedDeviceId && !selectedGroupId) {
          const deviceDetails = getAudioDeviceId()

          const { inputs: inputDevices } = await getEnumeratedDevices()

          if (deviceDetails?.deviceId && deviceDetails?.groupId) {
            const index = inputDevices?.findIndex(
              (device) => device?.deviceId === deviceDetails?.deviceId
            )

            if (index !== -1) {
              selectedGroupId = inputDevices?.[index]?.groupId || null
              selectedDeviceId = inputDevices?.[index]?.deviceId || null
            } else {
              selectedGroupId = inputDevices?.slice(-1)?.[0]?.groupId || null
              selectedDeviceId = inputDevices?.slice(-1)?.[0]?.deviceId || null
            }
          } else {
            selectedGroupId = inputDevices?.slice(-1)?.[0]?.groupId || null
            selectedDeviceId = inputDevices?.slice(-1)?.[0]?.deviceId || null
          }

          setSelectedAudioInputDeviceId(selectedDeviceId)
          selectedAudioInputGroupIdRef.current = selectedGroupId
          selectedAudioInputDeviceIdRef.current = selectedDeviceId
        }

        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: selectedDeviceId
                ? { exact: selectedDeviceId }
                : undefined,
              autoGainControl: true,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 48000,
              sampleSize: 16
            },
            video: needVideo
              ? {
                  width: { max: 640 },
                  height: { max: 480 },
                  frameRate: { max: 30 }
                }
              : false
          })
        } catch (e) {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: selectedDeviceId
                ? { exact: selectedDeviceId }
                : undefined,
              autoGainControl: true,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
              sampleRate: 48000,
              sampleSize: 16
            },
            video: needVideo
          })
        }

        if (stream?.getAudioTracks()?.[0]?.muted) {
          handleProducerPaused(true, 'audio')
        }

        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('mute', webcamAudioMuted)
        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('unmute', webcamAudioUnmuted)

        stream
          ?.getAudioTracks()?.[0]
          ?.addEventListener('ended', webcamAudioEnded)

        stream
          ?.getVideoTracks()?.[0]
          ?.addEventListener('ended', webcamVideoEnded)

        localStreamRef.current = stream
        setLocalStream(stream)

        return true
      } catch (error) {
        console.log('WEBCAM AUDIO ISSUE : ', error?.message || '')
        return false
      } finally {
        setGetAudioVideoLoading(false)
      }
    },
    [
      handleProducerPaused,
      getEnumeratedDevices,
      webcamAudioMuted,
      webcamVideoEnded,
      webcamAudioUnmuted,
      webcamAudioEnded
    ]
  )

  const joinRoom = useCallback(
    (roomName, worker, mediaServerEndpoint, stageId, attemptId) => {
      socket?.current?.emit(
        'joinRoom',
        {
          roomName,
          worker,
          userDetails: {
            ...(getUserDetails() || {}),
            ...(attemptId ? { attemptId } : {})
          }
        },
        (data) => {
          if (!data?.error) {
            setPeeerInfo((prev) => ({
              ...prev,
              roomName,
              ...(attemptId ? { attemptId } : {}),
              socketId: data?.socketId,
              webcamVideoStreamId: null,
              webcamAudioStreamId: null,
              screenStreamId: null,
              mediaServerEndpoint,
              worker
            }))
            setUserRoomJoined(true)
            routerCapabilities.current = data.rtpCapabilities
            createDevice()
          } else {
            socket?.current?.disconnect()
            excludeEndpointsRef.current = [
              ...excludeEndpointsRef.current,
              mediaServerEndpoint
            ]
            setRecallGetEndpoint({
              stageId,
              attemptId,
              call: true,
              excludeEndpoints: [...excludeEndpointsRef.current]
            })
          }
        }
      )
    },
    [createDevice]
  )

  const resetStates = useCallback((isErrorReset = false) => {
    if (!isErrorReset) {
      currentlySupervising.current = []
      setMediaState({
        audio: false,
        webcam: false,
        screen: false,
        handRaised: false
      })
      setSupervisedUser([])
    }

    previousMetadataRef.current = null

    device.current = null
    producerIds.current = []

    Object.values(consumingTransport?.current || {}).forEach((ct) => {
      ct?.consumer?.close()
    })

    consumerTransport?.current?.consumerTransport?.close()
    consumerTransport.current = null

    // producersTransport?.current?.close()
    producersTransport.current = null

    videoProducer?.current?.close()
    audioProducer?.current?.close()
    screenShareProducer?.current?.close()

    videoProducer.current = null
    audioProducer.current = null
    screenShareProducer.current = null

    consumerQueue.current = []
    consumingTransport.current = {}
    routerCapabilities.current = null

    if (screenMediaRecorderRef.current) {
      screenMediaRecorderRef.current?.stop()
      screenMediaRecorderRef.current = null
    }

    if (webcamMediaRecordRef.current) {
      webcamMediaRecordRef.current?.stop()
      webcamMediaRecordRef.current = null
    }

    setChats({})
    setNewStream({})
    setUpdatedChats({})
    setUserRoomJoined(false)
    setProducersTransportState(null)

    setPeeerInfo((prev) => ({
      ...prev,
      webcamAudioStreamId: null,
      webcamVideoStreamId: null,
      screenStreamId: null,
      properties: {
        ...(isErrorReset ? { ...(prev?.properties || {}) } : {}),
        producers: [],
        consumers: []
      }
    }))
  }, [])

  const initSocket = useCallback(
    (mediaSocketEndPoint, stageId, attemptId) => {
      // Resetting retry count to 0
      currentStageAttempIds.current = { stageId, attemptId }
      socketRetryCountRef.current = 0

      socket.current = io(
        MEDIA_SERVER_URL_MAP?.[mediaSocketEndPoint?.mediaServerEndpoint] ||
          mediaSocketEndPoint?.mediaServerEndpoint
      )

      socket.current.on('end-interview-room-response', ({ roomName }) => {
        setEndInterviewRoom(roomName)
      })

      socket.current.on('connection-success', () => {
        joinRoom(
          mediaSocketEndPoint?.roomName,
          mediaSocketEndPoint?.worker,
          mediaSocketEndPoint?.mediaServerEndpoint,
          stageId,
          attemptId
        )
      })

      socket.current.on('connect_error', (err) => {
        if (socketRetryCountRef.current > 1) {
          socket?.current?.disconnect()
          excludeEndpointsRef.current = [
            ...excludeEndpointsRef.current,
            mediaSocketEndPoint?.mediaServerEndpoint
          ]
          setRecallGetEndpoint({
            call: true,
            stageId,
            attemptId,
            excludeEndpoints: [...excludeEndpointsRef.current]
          })
        } else {
          socketRetryCountRef.current = socketRetryCountRef.current + 1
        }
      })

      socket.current.on('disconnect', (reason) => {
        resetStates(true)
      })

      socket.current.on('producer-closed', ({ remoteProducerId }) => {
        const producerToClose = consumingTransport?.current?.[remoteProducerId]

        producerToClose?.consumer?.close()

        delete consumingTransport?.current?.[remoteProducerId]

        setPeeerInfo((prev) => ({
          ...prev,
          properties: {
            ...(prev?.properties || {}),
            consumers:
              prev?.properties?.consumers?.filter(
                (consumer) => consumer?.producerId !== remoteProducerId
              ) || []
          }
        }))

        setNewStream((prev) => {
          const newData = { ...prev }
          delete newData?.[remoteProducerId]
          return newData
        })
      })

      socket.current.on('refresh-candidate', () => {
        socket?.current?.disconnect()
        setTimeout(() => {
          setRecallGetEndpoint({
            call: true,
            ...currentStageAttempIds.current,
            excludeEndpoints: [...excludeEndpointsRef.current]
          })
        }, 1000)
      })

      socket.current.on('mute-user-response', () => {
        setMediaState((prev) => ({ ...prev, audio: true }))
      })

      socket.current.on('consumed', async ({ params }) => {
        if (!params || params?.error) {
          return
        }

        if (!consumingTransport?.current?.[params?.producerId]) {
          return
        } else {
          const consumer =
            await consumerTransport?.current?.consumerTransport?.consume({
              id: params.id,
              producerId: params.producerId,
              kind: params.kind,
              rtpParameters: params.rtpParameters
            })

          consumingTransport.current[params?.producerId] = {
            ...(consumingTransport?.current[params?.producerId] || {}),
            consumer
          }

          const { track } = consumer

          setNewStream((prev) => {
            const newData = { ...prev }
            const currentData =
              consumingTransport?.current?.[params?.producerId]

            const closeStreamIndex = Object.values(newData || {}).findIndex(
              (stream) =>
                stream?.email === currentData?.email &&
                stream?.type === currentData?.type
            )

            if (closeStreamIndex !== -1) {
              closeConsumer(Object.keys(newData || {})?.[closeStreamIndex])
            }

            if (currentData) {
              newData[currentData?.producerId] = {
                socketId: currentData?.socketId,
                email: currentData?.email,
                name: currentData?.name,
                role: currentData?.role,
                authRole: currentData?.authRole,
                track: new MediaStream([track]),
                paused: false,
                type: currentData?.type,
                producerId: currentData?.producerId,
                hostname: currentData?.hostname,
                worker: currentData?.worker,
                roomName: currentData?.roomName
              }
            }

            return newData
          })
        }
      })
    },
    [joinRoom, resetStates, closeConsumer]
  )

  const closeMediaTracks = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop())
      localStreamRef.current = null
      setLocalStream(null)
    }

    if (localScreenShareStreamRef.current) {
      localScreenShareStreamRef.current
        .getTracks()
        .forEach((track) => track.stop())
      localScreenShareStreamRef.current = null
      setLocalScreenShareStream(null)
    }
  }, [])

  const closeSocket = useCallback(
    (reconnect = false) => {
      if (socket.current) {
        socket.current.disconnect()
      }

      if (!reconnect) {
        setConnectedUserList([])
        closeMediaTracks()
        resetStates()
      }
    },
    [resetStates, closeMediaTracks]
  )

  const updateDeviceId = useCallback(
    (deviceId, groupId) => {
      if (deviceId !== selectedAudioInputDeviceIdRef.current) {
        setGetAudioVideoLoading(true)
        setAudioDeviceId(deviceId, groupId)
        selectedAudioInputGroupIdRef.current = groupId
        selectedAudioInputDeviceIdRef.current = deviceId
        setSelectedAudioInputDeviceId(deviceId)
        endMediaProducer('audio')
        getAudioVideoStream(needVideoRef.current, deviceId, groupId, true)
      }
    },
    [endMediaProducer, getAudioVideoStream]
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeviceChange = useCallback(
    debounce(async () => {
      try {
        const { inputs } = await getEnumeratedDevices()

        // Compare the new device list with the last known state
        const newDeviceIds = inputs
          .map((device) => `${device.deviceId}-${device?.groupId}`)
          .sort()
          .join(',')
        const oldDeviceIds = devicesStateRef.current
          .map((device) => `${device.deviceId}-${device?.groupId}`)
          .sort()
          .join(',')

        if (newDeviceIds === oldDeviceIds) {
          return
        }

        devicesStateRef.current = inputs
      } catch (error) {
        console.log(error)
      }
    }, 300),
    []
  )

  // useEffects

  useEffect(() => {
    // Event listeners for online and offline events
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    setPeeerInfo((prev) => ({
      ...prev,
      properties: {
        ...(prev?.properties || {}),
        producersState: { ...mediaState }
      }
    }))
  }, [mediaState])

  useEffect(() => {
    if (userRoomJoined) {
      navigator?.mediaDevices?.addEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
    return () => {
      navigator?.mediaDevices?.removeEventListener(
        'devicechange',
        handleDeviceChange
      )
    }
  }, [handleDeviceChange, userRoomJoined])

  useEffect(() => {
    if (producersTransportState && userRoomJoined && localStream) {
      handleAudioVideoProducer()
    }
  }, [
    producersTransportState,
    userRoomJoined,
    localStream,
    handleAudioVideoProducer
  ])

  useEffect(() => {
    if (producersTransportState && userRoomJoined && localScreenShareStream) {
      handleScreenShareProducer()
    }
  }, [
    userRoomJoined,
    producersTransportState,
    localScreenShareStream,
    handleScreenShareProducer
  ])

  useEffect(() => {
    if (
      isOnline &&
      isSocketConnected &&
      socketAuthenticated &&
      peerInfo?.worker &&
      peerInfo?.roomName &&
      peerInfo?.mediaServerEndpoint
    ) {
      sendMessage('media-server/update-peer-info', peerInfo)
    }
  }, [isOnline, isSocketConnected, socketAuthenticated, peerInfo, sendMessage])

  useEffect(() => {
    if (isSocketConnected && socketAuthenticated && isOnline) {
      editorSocket.addEventListener(
        'message',
        handleMeetingEditorSocketMessages
      )
    }
    return () => {
      if (editorSocket) {
        editorSocket.removeEventListener(
          'message',
          handleMeetingEditorSocketMessages
        )
      }
    }
  }, [
    isOnline,
    isSocketConnected,
    socketAuthenticated,
    handleMeetingEditorSocketMessages,
    editorSocket
  ])

  useEffect(() => {
    if (
      isOnline &&
      userRoomJoined &&
      isSocketConnected &&
      socketAuthenticated
    ) {
      getAllChatMessages()
    }
  }, [
    isOnline,
    isSocketConnected,
    socketAuthenticated,
    userRoomJoined,
    getAllChatMessages
  ])

  useEffect(() => {
    if (
      isOnline &&
      isSocketConnected &&
      socketAuthenticated &&
      recallGetEndPoint.call
    ) {
      setRecallGetEndpoint((prev) => ({ ...prev, call: false }))
      getMediaServerEndPoint(
        recallGetEndPoint.attemptId,
        recallGetEndPoint.stageId,
        recallGetEndPoint.excludeEndpoints
      )
    }
  }, [
    isOnline,
    isSocketConnected,
    recallGetEndPoint,
    socketAuthenticated,
    getMediaServerEndPoint
  ])

  return (
    <MeetingContext.Provider
      value={{
        newStream,
        socket: socket.current,
        localStreamRef: localStreamRef.current,
        localScreenShareStreamRef: localScreenShareStreamRef.current,
        chats,
        isOnline,
        mediaState,
        localStream,
        updatedChats,
        closeConsumer,
        supervisedUser,
        userRoomJoined,
        endInterviewRoom,
        audioInputDevices,
        connectedUserList,
        audioOutputDevices,
        localScreenShareStream,
        selectedAudioInputDeviceId,
        initSocket,
        closeSocket,
        handleMuteUser,
        updateDeviceId,
        sendChatMessage,
        endMediaProducer,
        closeMediaTracks,
        setSupervisedUser,
        handleUpdateChats,
        handleChatRoomName,
        setEndInterviewRoom,
        getScreenShareSteam,
        getAudioVideoStream,
        getEnumeratedDevices,
        onRefreshStreamClick,
        handleProducerPaused,
        handleEndInterviewForAll,
        signalNewConsumerTransport,
        handleSuperviseCandidateSocket,
        handleUpdatePeerInfoProperties
      }}
    >
      <ErrorBoundary>{children}</ErrorBoundary>
    </MeetingContext.Provider>
  )
}

export default MeetingProvider
